import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GAME } from './data';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import VietnameseFont from './item/vietnameseFont.json';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import Answer from './answer';
import ConfirmButton from './confirmButton';
import sad from './item/sad.png';
import closeIcon from './item/close-button.png';
import tui from './item/tui.png';
import voucher from './item/voucher.png';
import newLead50 from './item/newLead50.png';
import newLead25 from './item/newLead25.png';
import newLead10 from './item/newLead10.png';
import newLead05 from './item/newLead05.png';
import hhh from './item/hhh.png';

const HEIGHT = 500;
const WIDTH = 500;

function Question({ camera, onClose }) {
  const loader = new FontLoader();
  const font = loader.parse(VietnameseFont);
  const textRef = useRef();
  const [randomItem, setRandomItem] = useState(GAME[0]);
  const [activeDiv, setActiveDiv] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [rewardImage, setRewardImage] = useState(null); // Thêm state cho ảnh phần thưởng

  // Load tất cả các texture
  const closeIconTexture = useLoader(THREE.TextureLoader, closeIcon);
  const sadTexture = useLoader(THREE.TextureLoader, sad); 

  const tuiTexture = useLoader(THREE.TextureLoader, tui);
  const voucherTexture = useLoader(THREE.TextureLoader, voucher);
  const newLead50Texture = useLoader(THREE.TextureLoader, newLead50);
  const newLead25Texture = useLoader(THREE.TextureLoader, newLead25);
  const newLead10Texture = useLoader(THREE.TextureLoader, newLead10);
  const newLead05Texture = useLoader(THREE.TextureLoader, newLead05);
  const hhhTexture = useLoader(THREE.TextureLoader, hhh);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * GAME.length);
    setRandomItem(GAME[randomIndex]);
  }, []);

  const geometry = useMemo(
    () =>
      new TextGeometry(randomItem.question, {
        font: font,
        size: 10,
        height: 0.5,
        depth: 1,
        curveSegments: 4,
        bevelThickness: 1,
        bevelSize: 0,
        bevelEnabled: true,
      }),
    // eslint-disable-next-line
    [randomItem]
  );
  geometry.computeBoundingBox();

  const centerOffset =
    -0.5 * (geometry.boundingBox.max.x - geometry.boundingBox.min.x);
  const centerOffsetY =
    0.5 * (geometry.boundingBox.max.y - geometry.boundingBox.min.y);

    const getRandomReward = () => {
      const randomValue = Math.random() * 256; // Tổng số phần thưởng là 256
      if (randomValue < 150) {
          return tuiTexture; // 150/256 là túi
      }
      if (randomValue < 200) {
          return voucherTexture; // 50/256 là voucher
      }
      if (randomValue < 203) {
          return newLead50Texture; // 3/256 là newLead50
      }
      if (randomValue < 206) {
          return newLead25Texture; // 3/256 là newLead25
      }
      if (randomValue < 226) {
          return newLead05Texture; // 20/256 là newLead05
      }
      if (randomValue < 246) {
          return newLead10Texture; // 20/256 là newLead10
      }
      return hhhTexture; // 10/256 là hhh
  };
  

  const handleConfirmAnswer = useCallback(() => {
    setIsShow(true);
    if (randomItem.correctkey === activeDiv) {
      setIsCorrect(true);
      setRewardImage(getRandomReward()); // Random ảnh phần thưởng
    } else {
      setIsCorrect(false);
    }
    // eslint-disable-next-line
  }, [randomItem, activeDiv]);

  useFrame((state) => {
    if (textRef.current) {
      if (state.gl.xr.isPresenting) {
        textRef.current.lookAt(camera.position);
      } else {
        textRef.current.quaternion.copy(camera.quaternion);
      }
    }

    const distanceFromCamera = 400;
    const target = new THREE.Vector3(0, 0, -distanceFromCamera);
    target.applyMatrix4(camera.matrixWorld);

    const moveSpeed = 20;
    const distance = textRef.current.position.distanceTo(target);
    if (distance > 0) {
      const amount = Math.min(moveSpeed, distance) / distance;
      textRef.current.position.lerp(target, amount);
    }
  });

  const onPointerEnter = useCallback(() => {
    document.body.style.cursor = 'pointer';
  }, []);

  const onPointerLeave = useCallback(() => {
    document.body.style.cursor = 'default';
  }, []);

  const handleDivClick = (index) => {
    setActiveDiv(index);
  };

  return (
    <group ref={textRef} scale={0.8}>
      {!isShow && (
        <>
          <mesh
            position={[centerOffset, (randomItem.aspect.height - 70) / 2, 0]}
          >
            <primitive object={geometry} />
            <meshBasicMaterial color={'black'} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <planeGeometry
              args={[randomItem.aspect.width, randomItem.aspect.height]}
            />
            <meshBasicMaterial color={'white'} />
          </mesh>

          {randomItem.answer.map((item, index) => (
            <group position={[0, 0, 0]} key={index}>
              <Answer
                text={item}
                camera={camera}
                index={index}
                correctkey={randomItem.correctkey}
                onClose={onClose}
                parent={geometry}
                textSpace={randomItem.textSpace}
                planeSpace={randomItem.planeSpace}
                space={randomItem.sapce}
                answerAspect={randomItem.answerAspect}
                isActive={activeDiv === index}
                onClick={handleDivClick}
              />
            </group>
          ))}
          <mesh
            position={[
              (randomItem.aspect.width - 30) / 2,
              (randomItem.aspect.height - 30) / 2,
              0,
            ]}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            onClick={() => onClose()}
          >
            <planeGeometry args={[20, 20]} />
            <meshBasicMaterial
              map={closeIconTexture}
              transparent
              side={THREE.DoubleSide}
              attach={`material`}
              alphaTest={0.5}
            />
          </mesh>

          <ConfirmButton
            top={randomItem.aspect.height}
            answerKey={randomItem.correctkey}
            answer={activeDiv}
            onConfirm={handleConfirmAnswer}
          />
        </>
      )}

      {isShow && (
        <>
          {isCorrect ? (
            <mesh position={[0, 0, 0]} onClick={() => onClose()}>
              <planeGeometry args={[WIDTH, HEIGHT]} />
              <meshBasicMaterial
                color={'white'}
                map={rewardImage} // Hiển thị ảnh phần thưởng random
                transparent
                alphaTest={0.1}
              />
            </mesh>
          ) : (
            <mesh position={[0, centerOffsetY, 0]} onClick={() => onClose()}>
              <planeGeometry args={[WIDTH, HEIGHT]} />
              <meshBasicMaterial
                color={'white'}
                map={sadTexture}
                transparent
                alphaTest={0.5}
              />
            </mesh>
          )}
        </>
      )}
    </group>
  );
}

export default Question;
